import React from 'react'
import moment from 'moment'
import { Box, Text, Display, FlexDirection } from '@gousto-internal/citrus-react'
import type { DeliveryDetailsContentBlock } from '@library/api-menu-experience'
import { formatDeliveryTime } from '../../../utils/formatters'
import { DeliveryDetailCard } from './DeliveryDetailCard'

type DeliveryDetailsProps = {
  deliverySlot: {
    date: string
    timeStart: string
    timeEnd: string
  }
  deliveryAddress: {
    name?: string
    line1: string
    line2?: string
    line3?: string
    town: string
    postcode: string
  }
}

const getAddressLines = (deliveryAddress: DeliveryDetailsProps['deliveryAddress']) =>
  [
    deliveryAddress.line1,
    deliveryAddress.line2,
    deliveryAddress.line3,
    deliveryAddress.town,
    deliveryAddress.postcode,
  ].filter((line): line is string => Boolean(line))

type DeliveryDetailsViewProps = Omit<DeliveryDetailsContentBlock, 'type'>

const DeliveryDetailsView = ({ date, address }: DeliveryDetailsViewProps) => {
  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Row}
      width="100%"
      gap={3}
      data-testid="delivery-details"
    >
      <DeliveryDetailCard iconName="calendar" title="Date">
        <Text size={2}>{date.formattedDate}</Text>
        <Text size={2}>{date.formattedTimeRange}</Text>
      </DeliveryDetailCard>

      <DeliveryDetailCard iconName="local_shipping" title="Address">
        {address.name && <Text size={2}>{address.name}</Text>}
        <Text
          size={2}
          aria-label={address.formattedAddress}
          title={address.formattedAddress}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {address.formattedAddress}
        </Text>
      </DeliveryDetailCard>
    </Box>
  )
}

export const ServerDrivenDeliveryDetails = ({
  contentBlock: { date, address },
}: {
  contentBlock: DeliveryDetailsContentBlock
}) => <DeliveryDetailsView date={date} address={address} />

export const DeliveryDetails = ({ deliverySlot, deliveryAddress }: DeliveryDetailsProps) => {
  const addressLines = getAddressLines(deliveryAddress)
  const fullAddress = addressLines.join(', ')

  return (
    <DeliveryDetailsView
      date={{
        formattedDate: moment(deliverySlot.date).format('dddd Do MMM'),
        formattedTimeRange: `${formatDeliveryTime(deliverySlot.timeStart)} - ${formatDeliveryTime(
          deliverySlot.timeEnd,
        )}`,
      }}
      address={{
        name: deliveryAddress.name,
        formattedAddress: fullAddress,
      }}
    />
  )
}
